import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Building2, Check, Copy, ExternalLink, KeyRound, X, AlertCircle, Trash2 } from 'lucide-react';
import BrandLogos from './BrandLogos';
import { COMPANY_BOARDS } from '../constants/jobBoards';

/**
 * Settings → "Your company's connected boards" (admin-only view).
 *
 * Lists the company-scoped boards (Bundesagentur, LinkedIn). For each board the
 * admin can connect, edit, or disconnect their own credentials. Once connected,
 * the unique per-company XML feed URL is exposed so the admin can paste it into
 * the board's employer portal.
 *
 * For prospects & recruiters the parent (Settings.js) doesn't render this card.
 */
const CompanyJobBoardsCard = ({ organizationId }) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  const backendBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

  const loadCatalog = async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const r = await axios.get(
        `${backendBase}/api/job-boards/catalog?organizationId=${organizationId}`
      );
      const all = r.data?.data || [];
      setCatalog(all.filter((b) => b.scope === 'organization'));
    } catch (e) {
      console.error('catalog load error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, [organizationId]);

  const startEdit = (board) => {
    setEditingKey(board.key);
    // Don't show previously-saved values (they're encrypted). Start fresh.
    setFormValues({});
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setFormValues({});
  };

  const handleField = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (board) => {
    try {
      setSaving(true);
      const cleanFields = (board.fields || []).filter((f) => f.type !== 'file');
      const credentials = {};
      cleanFields.forEach((f) => {
        if (formValues[f.key]) credentials[f.key] = formValues[f.key];
      });
      await axios.post(
        `${backendBase}/api/job-boards/credentials/${board.key}`,
        { organizationId, credentials }
      );
      setEditingKey(null);
      setFormValues({});
      await loadCatalog();
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed to save credentials');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (board) => {
    if (!window.confirm(`Disconnect ${board.name}? Your saved credentials will be deleted.`)) return;
    try {
      await axios.delete(
        `${backendBase}/api/job-boards/credentials/${board.key}?organizationId=${organizationId}`
      );
      await loadCatalog();
    } catch (e) {
      alert('Failed to disconnect.');
    }
  };

  const copyFeedUrl = (board) => {
    if (!board.feedUrl) return;
    navigator.clipboard?.writeText(board.feedUrl);
    setCopiedKey(board.key);
    setTimeout(() => setCopiedKey(null), 1800);
  };

  return (
    <Card data-testid="company-job-boards-card">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600" />
          Your company&apos;s job board accounts
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          These boards require your own account with the provider. Connect once
          here and JobastiIQ will publish to them automatically when you tick the
          board in the Publish modal.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {COMPANY_BOARDS.map((meta) => {
              const board = catalog.find((b) => b.key === meta.id);
              const connected = board?.connectionState === 'connected';
              const isEditing = editingKey === meta.id;
              return (
                <li key={meta.id} className="py-4" data-testid={`company-board-row-${meta.id}`}>
                  <div className="flex items-center gap-3">
                    {meta.inlineGlyph && BrandLogos[meta.inlineGlyph] ? (
                      React.createElement(BrandLogos[meta.inlineGlyph], { size: 32 })
                    ) : (
                      <div
                        className="w-8 h-8 rounded-md flex items-center justify-center text-white font-bold text-xs"
                        style={{ backgroundColor: meta.color }}
                      >
                        {meta.name.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{meta.name}</p>
                      <p className="text-[11px] text-gray-500 truncate">{meta.description}</p>
                    </div>
                    {connected ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                        <Check className="w-3 h-3" /> Connected
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">
                        Not connected
                      </span>
                    )}
                    {!isEditing && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => startEdit(board || { ...meta, key: meta.id, fields: undefined })}
                        data-testid={`company-board-edit-${meta.id}`}
                      >
                        <KeyRound className="w-3.5 h-3.5 mr-1" />
                        {connected ? 'Edit' : 'Connect'}
                      </Button>
                    )}
                  </div>

                  {/* Inline form */}
                  {isEditing && (
                    <div
                      className="mt-3 ml-11 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3"
                      data-testid={`company-board-form-${meta.id}`}
                    >
                      {(board?.fields || []).filter((f) => f.type !== 'file').map((f) => (
                        <div key={f.key}>
                          <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                            {f.label}
                            {f.required && <span className="text-red-500 ml-0.5">*</span>}
                          </label>
                          <input
                            type={f.type === 'password' ? 'password' : 'text'}
                            value={formValues[f.key] || ''}
                            onChange={(e) => handleField(f.key, e.target.value)}
                            className="w-full text-sm border border-gray-300 rounded px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            data-testid={`company-board-field-${meta.id}-${f.key}`}
                            placeholder={connected ? '•••••• (leave blank to keep saved)' : ''}
                          />
                        </div>
                      ))}
                      {meta.id === 'bundesagentur' && (
                        <p className="text-[10px] text-amber-600 flex items-start gap-1">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          .p12 certificate upload will be added in the next update. For now, please email it to JobastiIQ support after saving the other fields.
                        </p>
                      )}
                      <div className="flex items-center justify-between pt-1">
                        {board?.helpUrl && (
                          <a
                            href={board.helpUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            How to obtain credentials <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        <div className="flex items-center gap-2 ml-auto">
                          {connected && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDisconnect(board)}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`company-board-disconnect-${meta.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" /> Disconnect
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEdit}
                            data-testid={`company-board-cancel-${meta.id}`}
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSave(board || { key: meta.id, fields: [] })}
                            disabled={saving}
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            data-testid={`company-board-save-${meta.id}`}
                          >
                            {saving ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Feed URL block (only shown when connected) */}
                  {connected && !isEditing && board?.feedUrl && (
                    <div className="mt-3 ml-11 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-[11px] font-semibold text-gray-600 mb-1.5">
                        Your private feed URL — paste into {meta.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <code
                          className="flex-1 text-[11px] font-mono text-blue-700 bg-white border border-gray-200 rounded px-2 py-1.5 truncate"
                          data-testid={`company-board-feed-url-${meta.id}`}
                        >
                          {board.feedUrl}
                        </code>
                        <button
                          type="button"
                          onClick={() => copyFeedUrl(board)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded border border-blue-200"
                          data-testid={`company-board-copy-${meta.id}`}
                        >
                          {copiedKey === meta.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                          {copiedKey === meta.id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      {meta.id === 'linkedin' && (
                        <p className="text-[10px] text-gray-500 mt-2">
                          ⚠️ LinkedIn requires the company to have been a paying customer for ≥30 days before Basic Jobs activate.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default CompanyJobBoardsCard;
